import React from 'react';
import { NextUI } from '@/components/NextUI';
import { APP_EVENTS, logHubxEvent } from '@/app/providers/analytics';
import { formatAnalyticsSource } from '@/helpers/common';
import { buildStructuredAnalyticsSource } from '@/lib/analytics/utils/source';
import Icon from '@/components/Common/Icons/Icon';

import { useAppDispatch, useAppSelector } from '@/hooks/redux';
import { setUploadIntent } from '@/features/tool/slices/uploadSlice';
import { useGlobalUploadHandler } from '@/features/tool/hooks/useGlobalUploadHandler';
import { PDF_ONLY_FILE_TYPES } from '@/features/tool/utils/uploadConstraints';

const DownloadSuccessModal = ({
  data,
  hideModal,
}: {
  data?: {
    extension?: string;
    toolType?: string;
    originEntryPoint?: string;
    onDownload?: () => void | Promise<void>;
    onContinue?: () => void;
  };
  hideModal: () => void;
}) => {
  const dispatch = useAppDispatch();
  const { exportAsFiles } = useAppSelector((state) => state.tool);
  const { handleFiles } = useGlobalUploadHandler();
  const [isDownloading, setIsDownloading] = React.useState(false);
  const isDownloadingRef = React.useRef(false);
  const isMountedRef = React.useRef(true);

  React.useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const extension = (data?.extension || 'document').toLowerCase().replace(/^\./, '');

  const formattedToolType = React.useMemo(() => {
    return formatAnalyticsSource(data?.toolType);
  }, [data?.toolType]);
  const analyticsSource = React.useMemo(
    () =>
      buildStructuredAnalyticsSource(
        data?.toolType,
        data?.originEntryPoint
      ),
    [data?.originEntryPoint, data?.toolType]
  );

  const renderIcon = () => {
    const colors: Record<string, { light: string; dark: string; textPrefix: string }> = {
      pdf: { light: '#FF9F9F', dark: '#F43F3F', textPrefix: 'pdf' },
      docx: { light: '#9FBFFF', dark: '#3F7CF4', textPrefix: 'docx' },
      doc: { light: '#9FBFFF', dark: '#3F7CF4', textPrefix: 'docx' },
      xlsx: { light: '#A7F3D0', dark: '#059669', textPrefix: 'xlsx' },
      xls: { light: '#A7F3D0', dark: '#059669', textPrefix: 'xlsx' },
      pptx: { light: '#FED7AA', dark: '#EA580C', textPrefix: 'pptx' },
      ppt: { light: '#FED7AA', dark: '#EA580C', textPrefix: 'pptx' },
      jpg: { light: '#E9D5FF', dark: '#8B5CF6', textPrefix: 'jpg' },
      jpeg: { light: '#E9D5FF', dark: '#8B5CF6', textPrefix: 'jpg' },
      png: { light: '#E9D5FF', dark: '#8B5CF6', textPrefix: 'png' },
      zip: { light: '#D1D5DB', dark: '#4B5563', textPrefix: 'zip' },
      document: { light: '#D1D5DB', dark: '#4B5563', textPrefix: 'document' },
    };

    const color = colors[extension] || colors.document;

    return (
      <svg width="78" height="78" viewBox="0 0 78 78" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M74.6046 25.442V68.8527C74.6046 72.895 71.3277 76.1719 67.2854 76.1719H22.149C18.1067 76.1719 14.8298 72.895 14.8298 68.8527V9.14733C14.8298 5.10504 18.1067 1.82812 22.149 1.82812H52.3803L74.6046 25.442Z" fill={color.light} />
        <path d="M74.6043 25.4419H57.2595C54.5647 25.4419 52.3801 23.2573 52.3801 20.5624V1.82812L74.6043 25.4419Z" fill={color.dark} />
        <path d="M61.3768 65.1426H4.79585C4.02179 65.1426 3.39429 64.5151 3.39429 63.7411V38.098C3.39429 37.3239 4.02179 36.6964 4.79585 36.6964H61.3768C62.1508 36.6964 62.7783 37.3239 62.7783 38.098V63.7411C62.7783 64.5151 62.1508 65.1426 61.3768 65.1426Z" fill={color.dark} />
        {/* Text inside the icon block */}
        <text x="33" y="53" fill="white" fontSize="13" fontWeight="bold" fontFamily="Arial" textAnchor="middle">{color.textPrefix}</text>
      </svg>
    );
  };

  React.useEffect(() => {
    logHubxEvent({
      eventName: APP_EVENTS.LND_DOCUMENT_DOWNLOAD,
      source: formattedToolType,
      event_params: {
        source: analyticsSource,
      },
    });
  }, [analyticsSource, formattedToolType]);

  const handleManualDownload = async () => {
    if (isDownloadingRef.current) return;

    logHubxEvent({
      eventName: APP_EVENTS.BTN_DOWNLOAD_POPUP,
      source: formattedToolType,
      event_params: {
        source: analyticsSource,
      },
    });

    if (!data?.onDownload) return;

    isDownloadingRef.current = true;
    setIsDownloading(true);

    try {
      await data.onDownload();
    } catch (error) {
      console.error('Failed to download file from download popup:', error);
      isDownloadingRef.current = false;

      if (isMountedRef.current) {
        setIsDownloading(false);
      }

      return;
    }

    if (isMountedRef.current) {
      hideModal();
    }
  };

  const handleContinue = () => {
    logHubxEvent({
      eventName: APP_EVENTS.BTN_CONTINUE_POPUP,
      source: formattedToolType,
      event_params: {
        source: analyticsSource,
      },
    });

    if (data?.onContinue) {
      data.onContinue();
    } else if (exportAsFiles) {
      dispatch(
        setUploadIntent({
          targetPath: '/app/edit-pdf?tool=edit-pdf',
          fileTypes: PDF_ONLY_FILE_TYPES,
          multiple: false,
        })
      );
      handleFiles(exportAsFiles);
    }
    hideModal();
  };

  return (
    <NextUI.Modal
      className="max-w-[440px] rounded-2xl"
      classNames={{ content: 'py-10 px-8' }}
      defaultOpen={true}
      name={'DownloadSuccess'}
      backdrop="blur"
      hideCloseButton={false}
      onClose={hideModal}
    >
      <div className="flex flex-col items-center text-center">
        <div className="mb-6">
          {renderIcon()}
        </div>

        <h2 className="text-[24px] font-bold text-dark leading-tight mb-3">
          Your {extension === 'document' ? 'document' : extension.toUpperCase()} is ready for download
        </h2>

        <p className="text-gray-medium text-[15px] mb-8 leading-relaxed">
          The download starts automatically. If it doesn't, click "Download".
        </p>

        <div className="flex flex-col sm:flex-row gap-3 w-full">
          <NextUI.Button
            variant={'outline'}
            className="flex-1 h-12 border-gray-200 border border-primary-blue text-dark font-medium rounded-xl hover:bg-gray-50 transition-colors"
            onClick={handleContinue}
          >
            Continue editing
          </NextUI.Button>

          <NextUI.Button
            color="primary"
            className="flex-1 h-12 bg-primary-blue text-white font-medium rounded-xl transition-colors"
            isLoading={isDownloading}
            onClick={handleManualDownload}
          >
            Download {extension === 'document' ? 'Document' : extension.toUpperCase()}
          </NextUI.Button>
        </div>
      </div>
    </NextUI.Modal>
  );
};

export default DownloadSuccessModal;
